import React from 'react';

import { Box, Button, Typography } from '@material-ui/core';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import type { ActiveRopaReviewPanel, ActiveRopaReviewPanelActions } from 'config/api/ropaReviews/ropaReviews.types';
import processing_activities_messages from 'messages/processing_activities_messages';

import useStyles from '../ProcessingActivitiesAssetReviewsPage.styles';

type Props = {
  actions: ActiveRopaReviewPanelActions;
  onCancelReview: () => void;
  onCompleteReview: () => void;
  onEditReview: () => void;
  review: ActiveRopaReviewPanel;
  showAdminActions?: boolean;
};

const formatDate = (value: string) => {
  try {
    return format(new Date(value), 'yyyy-MM-dd');
  } catch {
    return value;
  }
};

const getRoundStatusClass = (status: string, styles: ReturnType<typeof useStyles>) => {
  switch (status) {
    case 'completed':
      return styles.statusBadgeSuccess;
    case 'cancelled':
      return styles.statusBadgeDanger;
    default:
      return styles.statusBadgeInfo;
  }
};

const ActiveRopaReviewPanelSection: React.FC<Props> = ({
  actions,
  onCancelReview,
  onCompleteReview,
  onEditReview,
  review,
  showAdminActions = true,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <section className={styles.reviewPanel}>
      <div className={styles.reviewPanelHeader}>
        <div>
          <div className={styles.reviewEyebrow}>{t(processing_activities_messages.asset_ropa.active_review.eyebrow)}</div>
          <Typography variant='h2'>{t(processing_activities_messages.asset_ropa.active_review.title)}</Typography>
          <div className={styles.reviewMeta}>
            <span>
              <strong>{t(processing_activities_messages.asset_ropa.active_review.initiated)}:</strong> {formatDate(review.initiatedAt)}
            </span>
            <span>
              <strong>{t(processing_activities_messages.asset_ropa.active_review.deadline)}:</strong> {formatDate(review.deadline)}
            </span>
            <span>
              <strong>{t(processing_activities_messages.asset_ropa.active_review.status)}:</strong>{' '}
              <span className={`${styles.statusBadge} ${getRoundStatusClass(review.status, styles)}`}>
                {t(processing_activities_messages.asset_ropa.round_status[review.status])}
              </span>
            </span>
            <span>
              <strong>{t(processing_activities_messages.asset_ropa.active_review.initiated_by)}:</strong>{' '}
              {review.initiatedByMembershipId
                ? t(processing_activities_messages.asset_ropa.active_review.membership_label, {
                    id: review.initiatedByMembershipId,
                  })
                : t(processing_activities_messages.asset_ropa.active_review.unknown_initiator)}
            </span>
          </div>
        </div>
        <Box className={styles.reviewActions}>
          {showAdminActions && actions.canEditDetails && (
            <Button onClick={onEditReview} variant='outlined'>
              {t(processing_activities_messages.asset_ropa.actions.edit_review)}
            </Button>
          )}
          {showAdminActions && actions.canCompleteReview && (
            <Button onClick={onCompleteReview} variant='outlined'>
              {t(processing_activities_messages.asset_ropa.actions.complete_review)}
            </Button>
          )}
          {showAdminActions && actions.canCancelReview && (
            <Button className={styles.destructiveAction} color='secondary' onClick={onCancelReview} variant='outlined'>
              {t(processing_activities_messages.asset_ropa.actions.cancel_review)}
            </Button>
          )}
        </Box>
      </div>
      <details className={styles.instructionsPanel}>
        <summary className={styles.instructionsSummary}>
          {t(processing_activities_messages.asset_ropa.active_review.instructions_summary)}
        </summary>
        <div className={styles.instructionsBody}>{review.reviewInstructions}</div>
      </details>
    </section>
  );
};

export default ActiveRopaReviewPanelSection;
